import { useCallback, useEffect, useMemo, useState } from 'react'
import { Loader2, Wallet } from 'lucide-react'

import { fetchMyCommissionSummary } from '@/lib/api/stylistWorkspace'
import { formatReportMoney, REPORT_PERIOD_OPTIONS, reportPeriodBounds } from '@/lib/api/reports'
import { apiErrorMessage } from '@/lib/apiErrorMessage'
import { useBranch } from '@/contexts/BranchContext'
import { cn } from '@/lib/utils'

export function StylistCommissionPage() {
  const { activeBranchId } = useBranch()
  const [period, setPeriod] = useState('month')
  const [summary, setSummary] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  const bounds = useMemo(() => reportPeriodBounds(period), [period])

  const loadSummary = useCallback(async () => {
    setLoading(true)
    setError('')

    try {
      const data = await fetchMyCommissionSummary({
        from: bounds.from,
        to: bounds.to,
      })
      setSummary(data)
    } catch (err) {
      setSummary(null)
      setError(apiErrorMessage(err, 'Unable to load your commission summary.'))
    } finally {
      setLoading(false)
    }
  }, [bounds.from, bounds.to])

  useEffect(() => {
    loadSummary()
  }, [loadSummary, activeBranchId])

  const report = summary?.report

  return (
    <div className="mx-auto max-w-[960px] space-y-6 px-4 py-8 lg:px-8">
      <div className="flex items-center gap-3">
        <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-primary/10 text-primary">
          <Wallet className="h-5 w-5" aria-hidden />
        </div>
        <div>
          <h1 className="text-xl font-semibold tracking-tight">My commissions</h1>
          <p className="text-sm text-muted-foreground">
            {summary?.stylist?.name
              ? `Earnings for ${summary.stylist.name} on the active branch.`
              : 'Your commission summary for the selected period.'}
          </p>
        </div>
      </div>

      <section className="card-surface p-6 lg:p-8">
        <div className="mb-5 flex flex-wrap gap-2">
          {REPORT_PERIOD_OPTIONS.map((option) => (
            <button
              key={option.id}
              type="button"
              onClick={() => setPeriod(option.id)}
              className={cn(
                'rounded-xl px-3 py-1.5 text-sm font-medium transition-colors',
                period === option.id
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted/70 hover:bg-muted',
              )}
            >
              {option.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex items-center gap-2 py-8 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading summary…
          </div>
        ) : error ? (
          <p className="text-sm text-destructive" role="alert">
            {error}
          </p>
        ) : report ? (
          <>
            <div className="mb-5 grid gap-3 sm:grid-cols-3">
              <SummaryCard label="Entries" value={String(report.summary.entry_count)} />
              <SummaryCard
                label="Commission total"
                value={`AED ${formatReportMoney(report.summary.commission_total)}`}
              />
              <SummaryCard
                label="Paid out"
                value={`AED ${formatReportMoney(report.summary.paid_total)}`}
              />
            </div>

            <div className="overflow-x-auto rounded-2xl border border-border/70">
              <table className="min-w-full text-sm">
                <thead className="bg-muted/40 text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <tr>
                    <th className="px-4 py-3 font-medium">Sale</th>
                    <th className="px-4 py-3 font-medium">Net</th>
                    <th className="px-4 py-3 font-medium">Commission</th>
                    <th className="px-4 py-3 font-medium">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {report.entries.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="px-4 py-8 text-center text-muted-foreground">
                        No commission entries in this period.
                      </td>
                    </tr>
                  ) : (
                    report.entries.map((entry) => (
                      <tr key={entry.id} className="border-t border-border/60">
                        <td className="px-4 py-3">{entry.sale_number ?? `#${entry.sale_id}`}</td>
                        <td className="px-4 py-3">AED {formatReportMoney(entry.commissionable_amount)}</td>
                        <td className="px-4 py-3">AED {formatReportMoney(entry.commission_amount)}</td>
                        <td className="px-4 py-3 capitalize">{entry.status}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </>
        ) : null}
      </section>
    </div>
  )
}

/**
 * @param {{ label: string, value: string }} props
 */
function SummaryCard({ label, value }) {
  return (
    <div className="rounded-2xl border border-border/70 bg-muted/20 px-4 py-3">
      <p className="text-xs uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className="mt-1 text-lg font-semibold">{value}</p>
    </div>
  )
}
